import * as React from "react"
import { Card, CardContent } from "./card"
import { Separator } from "./separator"
import { Badge } from "./badge"
import { Button } from "./button"
import { cn } from "../lib/utils"

export interface MedicationCardProps {
  /**
   * The name of the medication
   */
  medicationName: string
  /**
   * The dosage of the medication (e.g., "1mg", "50mg", "200mg/ml")
   */
  dosage: string
  /**
   * The supply information (e.g., "30 day supply", "90 tablets", "1 month")
   */
  supply: string
  /**
   * Optional status for the medication
   */
  status?: 'pending' | 'approved' | 'denied' | 'discontinued' | 'active' | 'shipped' | 'delivered' | 'scheduled' | 'needs_review'
  /**
   * Whether this is an async appointment/visit
   */
  isAsync?: boolean
  /**
   * Optional order date for Orders variant
   */
  orderDate?: string
  /**
   * Optional approval ID for Orders variant  
   */
  approvalId?: string
  /**
   * Optional order number for Preferred variant
   */
  orderNumber?: string
  /**
   * Optional estimated delivery date
   */
  estimatedDelivery?: string
  /**
   * Optional fulfillment status
   */
  fulfillmentStatus?: string
  /**
   * Optional next visit date
   */
  nextVisit?: string
  /**
   * Optional next prescription due date (ISO string)
   */
  nextPrescriptionDue?: string
  /**
   * Optional edit handler - shows edit link when provided
   */
  onEdit?: () => void
  /**
   * Optional title click handler - makes title a clickable link when provided
   */
  onTitleClick?: () => void
  /**
   * Optional additional CSS classes
   */
  className?: string
  /**
   * Click handler for the medication card
   */
  onClick?: () => void
  /**
   * Disable hover effects (shadow, scale) - useful for compound cards
   */
  disableHover?: boolean
}

export function MedicationCard({
  medicationName,
  dosage,
  supply,
  status,
  isAsync,
  orderDate,
  approvalId,
  orderNumber,
  estimatedDelivery,
  fulfillmentStatus,
  nextVisit,
  nextPrescriptionDue,
  onEdit,
  onTitleClick,
  className,
  onClick,
  disableHover = false
}: MedicationCardProps) {
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'approved':
      case 'active':
      case 'delivered':
      case 'scheduled':
        return 'default' as const
      case 'pending':
      case 'needs_review':
      case 'shipped':
        return 'secondary' as const
      case 'denied':
      case 'discontinued':
        return 'destructive' as const
      default:
        return 'outline' as const
    }
  }

  // Check if estimated delivery date has passed
  const hasEstimatedDeliveryPassed = React.useMemo(() => {
    if (!estimatedDelivery) return false
    const today = new Date()
    const deliveryDate = new Date(estimatedDelivery)
    // Set both dates to start of day for accurate comparison
    today.setHours(0, 0, 0, 0)
    deliveryDate.setHours(0, 0, 0, 0)
    return deliveryDate < today
  }, [estimatedDelivery])


  // Check if title should be clickable
  const shouldAllowTitleClick = React.useMemo(() => {
    if (!onTitleClick) return false
    
    // If no edit handler is provided, this card is likely being used for non-medication content (e.g., visits)
    // In this case, always allow title clicks
    if (!onEdit) {
      return true
    }
    
    // Always allow click when status is pending or needs review (can always edit pending medications)
    if (status === 'pending' || status === 'needs_review') {
      return true
    }
    
    // For all other statuses, allow click
    return true
  }, [onTitleClick, status, onEdit])

  return (
    <Card 
      className={cn(
        "w-full transition-all duration-200", 
        !disableHover && "hover:shadow-md",
        !className?.includes('w-full') && "max-w-sm",
        onClick && !disableHover && "cursor-pointer hover:scale-[1.02]",
        onClick && disableHover && "cursor-pointer",
        className
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="space-y-1">
          <div className="flex items-start justify-between gap-2">
            {shouldAllowTitleClick ? (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onTitleClick!()
                }}
                className="text-base font-semibold leading-none tracking-tight text-foreground hover:underline text-left"
              >
                {medicationName}
              </button>
            ) : (
              <h3 className="text-base font-semibold leading-none tracking-tight text-foreground">
                {medicationName}
              </h3>
            )}
            <div className="flex items-center gap-2">
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit()
                  }}
                  className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                >
                  Edit
                </button>
              )}
              {isAsync && (
                <Badge
                  variant="outline"
                  className="text-xs"
                >
                  async
                </Badge>
              )}
              {status && (
                <Badge
                  variant={getStatusBadgeVariant(status)}
                  className="capitalize text-xs"
                >
                  {status}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center text-sm text-muted-foreground">
            <span>{dosage}</span>
            <Separator orientation="vertical" className="mx-2 h-4" />
            <span>{supply}</span>
          </div>
          {(orderDate || approvalId || orderNumber || estimatedDelivery || fulfillmentStatus || nextVisit || (nextPrescriptionDue && status === 'approved')) && (
            <div className="flex items-center text-xs text-muted-foreground pt-1">
              {orderNumber && (
                <>
                  <span>{orderNumber}</span>
                  {(approvalId || orderDate || estimatedDelivery || nextVisit) && <Separator orientation="vertical" className="mx-2 h-3" />}
                </>
              )}
              {approvalId && (
                <>
                  <span>Approval: {approvalId.slice(0, 8)}...</span>
                  {(orderDate || estimatedDelivery || nextVisit) && <Separator orientation="vertical" className="mx-2 h-3" />}
                </>
              )}
              {orderDate && (
                <>
                  <span>Ordered: {orderDate}</span>
                  {((fulfillmentStatus && fulfillmentStatus !== 'delivered' && !hasEstimatedDeliveryPassed) || (estimatedDelivery && !hasEstimatedDeliveryPassed) || nextVisit || (nextPrescriptionDue && status === 'approved')) && <Separator orientation="vertical" className="mx-2 h-3" />}
                </>
              )}
              {fulfillmentStatus && fulfillmentStatus !== 'delivered' && !hasEstimatedDeliveryPassed && (
                <>
                  <span>Order Status: {fulfillmentStatus === 'pharmacy_fulfilled' ? 'Pharmacy Fulfilled' : fulfillmentStatus.charAt(0).toUpperCase() + fulfillmentStatus.slice(1)}</span>
                  {(estimatedDelivery || nextVisit || (nextPrescriptionDue && status === 'approved')) && <Separator orientation="vertical" className="mx-2 h-3" />}
                </>
              )}
              {estimatedDelivery && !hasEstimatedDeliveryPassed && (
                <>
                  <span>Estimated Delivery: {new Date(estimatedDelivery).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit', 
                    year: 'numeric'
                  })}</span>
                  {(nextVisit || (nextPrescriptionDue && status === 'approved')) && <Separator orientation="vertical" className="mx-2 h-3" />}
                </>
              )}
              {nextVisit && (
                <>
                  <span>Next Visit: {new Date(nextVisit).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}</span>
                  {nextPrescriptionDue && status === 'approved' && <Separator orientation="vertical" className="mx-2 h-3" />}
                </>
              )}
              {nextPrescriptionDue && status === 'approved' && (
                <span>Next Refill Due: {new Date(nextPrescriptionDue).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}</span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}